/**
 * PetiteEnfanceLayout — shell partagé du module Petite enfance.
 *
 * Jumeau de ScolaireLayout, accent « petite_enfance » (rose). Garantit que la
 * sub-nav reste affichée et que les sections se retrouvent aux mêmes endroits
 * que le scolaire (Tableau de bord · Planning · Pointage · … · Tarifs ·
 * Calendrier · Paramètres). Les onglets propres à la petite enfance
 * (Structures, RAM, Pré-inscriptions) sont conservés.
 *
 * Deux jeux d'onglets selon le rôle :
 *   - manager / admin / super_admin : pilotage complet
 *   - agent : vue terrain réduite (présences du jour + opérationnel)
 * Les sections de configuration (Tarifs, Calendrier, Paramètres) sont réservées
 * au maire / adjoints (admin) — comme le scolaire.
 */

import { type ReactNode, useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { AppHeader } from '@/components/AppHeader';

export type PetiteEnfanceTab =
  | 'ensemble' | 'planning-travail' | 'presence-agents' | 'pointage' | 'dossiers' | 'contrats'
  | 'presences' | 'facturation' | 'structures' | 'ram' | 'preinscriptions'
  | 'tarifs' | 'calendrier' | 'parametres';

export function PetiteEnfanceLayout({
  activeTab,
  children,
  backLink,
}: {
  activeTab: PetiteEnfanceTab;
  children: ReactNode;
  backLink?: { to: string; label: string };
}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAgent = user?.role === 'agent';
  const isAdmin = !!user && ['super_admin', 'admin'].includes(user.role);

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Petite enfance" accent="petite_enfance" />

      <nav
        aria-label="Sections petite enfance"
        className="sticky top-[57px] z-20 border-b border-border bg-surface/60 backdrop-blur-glass"
      >
        <div className="flex flex-wrap items-center gap-1 px-4 py-2 sm:px-6">
          {backLink && (
            <button
              type="button"
              onClick={() => navigate(backLink.to)}
              className="mr-2 inline-flex h-9 items-center gap-1 rounded-r-md px-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface/60 hover:text-petite_enfance-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petite_enfance focus-visible:ring-offset-2 focus-visible:ring-offset-bg whitespace-nowrap"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {backLink.label}
            </button>
          )}

          {isAgent ? (
            <>
              <MenuDeroulant
                label="Suivi quotidien"
                active={activeTab === 'planning-travail' || activeTab === 'presence-agents' || activeTab === 'pointage' || activeTab === 'presences'}
                items={[
                  { to: '/petite-enfance/mon-planning', label: 'Planning' },
                  { to: '/petite-enfance/presences', label: 'Pointage' },
                ]}
                onNavigate={navigate}
              />
              <MenuDeroulant
                label="Dossiers"
                active={activeTab === 'dossiers' || activeTab === 'contrats' || activeTab === 'ram'}
                items={[
                  { to: '/petite-enfance/dossiers', label: 'Dossiers & enfants' },
                  { to: '/petite-enfance/contrats', label: 'Contrats' },
                  { to: '/petite-enfance/ram', label: 'RAM' },
                ]}
                onNavigate={navigate}
              />
            </>
          ) : (
            <>
              <Pill to="/petite-enfance" label="Tableau de bord" active={activeTab === 'ensemble'} end />
              <MenuDeroulant
                label="Suivi quotidien"
                active={activeTab === 'planning-travail' || activeTab === 'presence-agents' || activeTab === 'pointage' || activeTab === 'presences'}
                items={[
                  { to: '/petite-enfance/planning-travail', label: 'Planning' },
                  { to: '/petite-enfance/presences', label: 'Pointage' },
                ]}
                onNavigate={navigate}
              />
              <MenuDeroulant
                label="Dossiers"
                active={activeTab === 'dossiers' || activeTab === 'contrats' || activeTab === 'ram' || activeTab === 'preinscriptions'}
                items={[
                  { to: '/petite-enfance/dossiers', label: 'Dossiers & enfants' },
                  { to: '/petite-enfance/contrats', label: 'Contrats' },
                  { to: '/petite-enfance/ram', label: 'RAM' },
                  { to: '/petite-enfance/preinscriptions', label: 'Pré-inscriptions' },
                ]}
                onNavigate={navigate}
              />
              <Pill to="/petite-enfance/facturation" label="Facturation" active={activeTab === 'facturation'} />
              {isAdmin && (
                <MenuDeroulant
                  label="Configuration"
                  active={activeTab === 'structures' || activeTab === 'tarifs' || activeTab === 'calendrier' || activeTab === 'parametres'}
                  items={[
                    { to: '/petite-enfance/structures', label: 'Structures' },
                    { to: '/petite-enfance/tarifs', label: 'Tarifs' },
                    { to: '/petite-enfance/calendrier', label: 'Calendrier' },
                    { to: '/petite-enfance/parametres', label: 'Paramètres' },
                    { to: '/petite-enfance/prestataires', label: 'Prestataires délégués' },
                  ]}
                  onNavigate={navigate}
                />
              )}
            </>
          )}
        </div>
      </nav>

      {children}
    </div>
  );
}

function Pill({
  to, label, active, end,
}: {
  to: string;
  label: string;
  active: boolean;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={() =>
        [
          'inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium transition-all whitespace-nowrap',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petite_enfance focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          active ? 'bg-petite_enfance text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70 hover:text-ink',
        ].join(' ')
      }
    >
      {label}
    </NavLink>
  );
}

function MenuDeroulant({
  label, active, items, onNavigate,
}: {
  label: string;
  active: boolean;
  items: { to: string; label: string }[];
  onNavigate: (to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function toggle() {
    setOpen((o) => {
      const next = !o;
      if (next && btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 4, left: r.left });
      }
      return next;
    });
  }
  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(t) && btnRef.current && !btnRef.current.contains(t)) setOpen(false);
    }
    function onScrollResize() { setOpen(false); }
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className={[
          'inline-flex h-9 items-center gap-1 rounded-r-pill px-4 text-sm font-medium transition-all whitespace-nowrap',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petite_enfance focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          active ? 'bg-petite_enfance text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70 hover:text-ink',
        ].join(' ')}
      >
        {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="min-w-[200px] rounded-r-md border border-border bg-surface shadow-r-lg p-1"
        >
          {items.map((it) => (
            <button
              key={it.to}
              role="menuitem"
              type="button"
              onClick={() => { setOpen(false); onNavigate(it.to); }}
              className="block w-full rounded-r-sm px-3 py-2 text-left text-xs text-ink hover:bg-petite_enfance-tint/40"
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
